import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

// Components
import { BigButtons } from '../../reusable/buttons/Buttons';
import SelectWithPicture from '../../reusable/SelectWithPicture';
import DropdownWithCheckBox from '../Custom/DropdownWithCheckBox';
import AvailabilitySection from './components/AvailabilitySection';
import ClubSection from './components/ClubSection';
import ProfileSection from './components/ProfileSection';

// Hooks and Utils
import { getAlphaPassportOptions } from '../../../utils/helpers';
import ConfirmExitModal from '../../reusable/ConfirmExitModal';
import { useEditProfileForm } from './hooks/useEditProfileForm';

const EditProfileForm = ({ uploading }) => {
    const userInfo = useSelector(state => state.user.data);
    const navigate = useNavigate();
    const formRef = useRef(null);
    // Track previous error state to detect when new errors appear (like on form submit)
    const prevErrorsRef = useRef({});
    const [showConfirmModal, setShowConfirmModal] = useState(false);

    const {
        formik,
        isFormActuallyDirty,
        teams,
        setTeams,
        searchValue,
        setSearchValue,
        selectTeam,
        setSelectTeam,
        selectClub,
        setSelectClub,
        isFetchingTeams,
        errorMessage,
        setNotFoundClubError,
        dateThreshold,
        loadingUserUpdate,
        handleTeamChange,
        handleRoleChange,
        getTeamsBasedOnSelectedClub,
        PlayerPosition,
        PreferredFoot,
        cleanDate,
        listOfNationalities
    } = useEditProfileForm(userInfo, navigate);

    const {
        values,
        errors,
        touched,
        handleBlur,
        handleChange,
        handleSubmit,
        setFieldValue
    } = formik;

    // Handle browser refresh/close warning
    useEffect(() => {
        const handleBeforeUnload = event => {
            if (isFormActuallyDirty && !loadingUserUpdate) {
                event.preventDefault();
                event.returnValue =
                    'You have unsaved changes. Are you sure you want to leave?';
                return event.returnValue;
            }
        };

        if (isFormActuallyDirty && !loadingUserUpdate) {
            window.addEventListener('beforeunload', handleBeforeUnload);
        }

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [isFormActuallyDirty, loadingUserUpdate]);

    // Handle back button navigation
    useEffect(() => {
        const handlePopState = event => {
            if (isFormActuallyDirty && !loadingUserUpdate) {
                // Prevent the back navigation
                window.history.pushState(null, '', window.location.href);

                setShowConfirmModal(true);
            }
        };

        if (isFormActuallyDirty && !loadingUserUpdate) {
            // Push state to catch back button
            window.history.pushState(null, '', window.location.href);
            window.addEventListener('popstate', handlePopState);
        }

        return () => {
            window.removeEventListener('popstate', handlePopState);
        };
    }, [isFormActuallyDirty, loadingUserUpdate]);

    const handleConfirmSave = async () => {
        try {
            setShowConfirmModal(false);

            await formik.submitForm();
        } catch (error) {
            console.error('Error saving form:', error);
            setShowConfirmModal(true);
        }
    };

    const handleModalCancel = () => {
        formik.resetForm();
        setShowConfirmModal(false);
        navigate('/profile');
    };

    // Function to scroll to first error field
    const scrollToFirstError = useCallback(() => {
        if (!formRef.current || Object.keys(errors).length === 0) return;

        // Find the first field with an error (only touched fields)
        const errorFields = Object.keys(errors).filter(field => touched[field]);
        if (errorFields.length === 0) return;

        // Try to find the first error field in the DOM
        for (const fieldName of errorFields) {
            // Look for the field wrapper first, then the input element
            let errorElement = formRef.current.querySelector(
                `#field-${fieldName}`
            );

            if (!errorElement) {
                // Fallback: look for input/textarea/select with the field name
                errorElement = formRef.current.querySelector(
                    `[name="${fieldName}"], #${fieldName}, [data-field="${fieldName}"]`
                );
            }

            if (errorElement) {
                // Scroll to the element with some offset
                const yOffset = -120; // Adjust this value as needed for header space
                const elementPosition =
                    errorElement.getBoundingClientRect().top +
                    window.pageYOffset;
                const offsetPosition = elementPosition + yOffset;

                window.scrollTo({
                    top: offsetPosition,
                    behavior: 'smooth'
                });

                // Try to focus the actual input element within the field
                const inputElement = errorElement.querySelector(
                    'input, textarea, select, [tabindex]'
                );
                if (
                    inputElement &&
                    inputElement.focus &&
                    typeof inputElement.focus === 'function'
                ) {
                    setTimeout(() => {
                        inputElement.focus();
                    }, 400); // Small delay to allow smooth scroll to complete
                }

                // Add a visual highlight to the error field
                errorElement.classList.add('error-highlight');
                setTimeout(() => {
                    errorElement.classList.remove('error-highlight');
                }, 1000);

                break;
            }
        }
    }, [errors, touched]);

    // Enhanced form submit handler
    const enhancedHandleSubmit = e => {
        e.preventDefault();

        handleSubmit(e);

        setTimeout(() => {
            if (Object.keys(errors).length > 0) {
                scrollToFirstError();
            }
        }, 100);
    };

    useEffect(() => {
        const currentErrorCount = Object.keys(errors).length;
        const previousErrorCount = Object.keys(prevErrorsRef.current).length;

        // If we have new errors (like after form submission), scroll to first error
        if (currentErrorCount > 0 && currentErrorCount > previousErrorCount) {
            setTimeout(() => {
                scrollToFirstError();
            }, 50);
        }

        prevErrorsRef.current = errors;
    }, [errors, scrollToFirstError]);

    return (
        <div>
            {Object.keys(errors).length > 0 && (
                <p className='text-red-600 mb-4 text-center italic'>
                    *Required fields must be filled in before saving.
                </p>
            )}
            <form ref={formRef} onSubmit={enhancedHandleSubmit}>
                <ProfileSection
                    values={values}
                    errors={errors}
                    touched={touched}
                    handleChange={handleChange}
                    handleBlur={handleBlur}
                    setFieldValue={setFieldValue}
                    userInfo={userInfo}
                    handleRoleChange={handleRoleChange}
                    dateThreshold={dateThreshold}
                    DropdownWithCheckBox={DropdownWithCheckBox}
                    PreferredFoot={PreferredFoot}
                    PlayerPosition={PlayerPosition}
                    cleanDate={cleanDate}
                />

                <ClubSection
                    values={values}
                    errors={errors}
                    touched={touched}
                    handleChange={handleChange}
                    handleBlur={handleBlur}
                    setFieldValue={setFieldValue}
                    userInfo={userInfo}
                    teams={teams}
                    selectTeam={selectTeam}
                    selectClub={selectClub}
                    searchValue={searchValue}
                    errorMessage={errorMessage}
                    isFetchingTeams={isFetchingTeams}
                    handleTeamChange={handleTeamChange}
                    setSelectClub={setSelectClub}
                    setTeams={setTeams}
                    setSelectTeam={setSelectTeam}
                    setNotFoundClubError={setNotFoundClubError}
                    setSearchValue={setSearchValue}
                    getTeamsBasedOnSelectedClub={getTeamsBasedOnSelectedClub}
                    SelectWithPicture={SelectWithPicture}
                />

                <AvailabilitySection
                    values={values}
                    errors={errors}
                    touched={touched}
                    handleChange={handleChange}
                    handleBlur={handleBlur}
                    userInfo={userInfo}
                    ListOfNationalities={listOfNationalities}
                    handleRoleChange={handleRoleChange}
                    DropdownWithCheckBox={DropdownWithCheckBox}
                    ListOfCountries={getAlphaPassportOptions() || []}
                />

                {/* Only show save button if form has changes and user info is loaded */}
                {isFormActuallyDirty && userInfo?.id && (
                    <div className='mb-2 mt-16'>
                        <BigButtons
                            isDisabled={uploading || isFetchingTeams}
                            black
                            label={uploading ? 'Uploading' : 'SAVE'}
                            isLoading={loadingUserUpdate}
                        />
                    </div>
                )}

                {/* Confirm Exit Modal */}
                {showConfirmModal && (
                    <ConfirmExitModal
                        isOpen={showConfirmModal}
                        onConfirm={handleConfirmSave}
                        onCancel={handleModalCancel}
                    />
                )}
            </form>
        </div>
    );
};

export default EditProfileForm;
